import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ENERGY_CONTROL_MONTHLY_PRICE_EUR, formatEur } from '../../config/pricing';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2 } from 'lucide-react';

const API = '/api';

const CONFIG = {
  start_package: {
    title: 'Stel mijn startpakket samen',
    description:
      'Op basis van uw antwoorden zetten we een concreet startpakket voor u klaar. Geen abonnement, vrijblijvend.',
    submitLabel: 'Startpakket aanvragen',
    successMsg: 'Bedankt — we maken uw startpakketadvies klaar en sturen het binnen 2 werkdagen.',
    showPostcode: true,
    showPhone: false,
    placeholderMessage: 'Optioneel: aandachtspunten of vragen over uw startpakket',
  },
  installation: {
    title: 'Vraag veilige installatie aan',
    description:
      'We koppelen u aan een gecertificeerde Powersure-installateur die uw situatie vrijblijvend bekijkt.',
    submitLabel: 'Installatie aanvragen',
    successMsg: 'Bedankt — een installateur neemt binnen 2 werkdagen contact met u op.',
    showPostcode: true,
    showPhone: true,
    placeholderMessage: 'Optioneel: wat moet er geïnstalleerd worden? (groepenkast, boiler, laadpaal, …)',
  },
  monitoring: {
    title: 'Activeer optionele maandelijkse monitoring',
    description:
      `Ontvang elke maand een Powersure inzichtsrapport. Optioneel na uw eenmalige Energy Control Plan. Richtprijs: ${formatEur(ENERGY_CONTROL_MONTHLY_PRICE_EUR)} per maand. Wij nemen contact met u op voor activering — er volgt geen automatische afschrijving.`,
    submitLabel: 'Monitoring interesse tonen',
    successMsg: 'Bedankt — we nemen contact met u op om monitoring te activeren.',
    showPostcode: false,
    showPhone: false,
    placeholderMessage: 'Optioneel: wat zou u graag in uw maandrapport zien?',
  },
};

export default function EnergyControlCTAModal({ open, onClose, ctaType, intakeId }) {
  const cfg = CONFIG[ctaType];
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [postcode, setPostcode] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setName(''); setEmail(''); setPhone(''); setPostcode(''); setMessage('');
    setErrors({}); setDone(false); setLoading(false);
  };

  const close = () => { reset(); onClose(); };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Vul uw naam in';
    if (!email.trim()) e.email = 'Vul uw e-mailadres in';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Ongeldig e-mailadres';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/energy-control/cta-lead`, {
        intake_id: intakeId || null,
        cta_type: ctaType,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        postcode: postcode.trim() || null,
        message: message.trim() || null,
      });
      setDone(true);
      toast.success('Aanvraag verzonden');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  if (!cfg) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid={`ec-cta-modal-${ctaType}`}>
        {done ? (
          <div className="py-4 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-primary" />
            </div>
            <DialogHeader className="text-center">
              <DialogTitle className="text-xl text-primary text-center" style={{ fontFamily: 'Outfit' }}>
                Aanvraag ontvangen
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-2">
                {cfg.successMsg}
              </DialogDescription>
            </DialogHeader>
            <button
              type="button"
              onClick={close}
              className="btn-primary mt-5 inline-flex items-center justify-center px-6 py-2 rounded-md"
              data-testid={`ec-cta-modal-close-${ctaType}`}
            >
              Sluiten
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl text-primary" style={{ fontFamily: 'Outfit' }}>
                {cfg.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {cfg.description}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor={`ec-cta-name-${ctaType}`} className="mb-1.5 block">Naam *</Label>
                <Input
                  id={`ec-cta-name-${ctaType}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid={`ec-cta-name-${ctaType}`}
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor={`ec-cta-email-${ctaType}`} className="mb-1.5 block">E-mailadres *</Label>
                <Input
                  id={`ec-cta-email-${ctaType}`}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid={`ec-cta-email-${ctaType}`}
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>

              {cfg.showPhone && (
                <div>
                  <Label htmlFor={`ec-cta-phone-${ctaType}`} className="mb-1.5 block">Telefoon (optioneel)</Label>
                  <Input
                    id={`ec-cta-phone-${ctaType}`}
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid={`ec-cta-phone-${ctaType}`}
                  />
                </div>
              )}

              {cfg.showPostcode && (
                <div>
                  <Label htmlFor={`ec-cta-postcode-${ctaType}`} className="mb-1.5 block">Postcode (optioneel)</Label>
                  <Input
                    id={`ec-cta-postcode-${ctaType}`}
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    placeholder="1234 AB"
                    data-testid={`ec-cta-postcode-${ctaType}`}
                  />
                </div>
              )}

              <div>
                <Label htmlFor={`ec-cta-message-${ctaType}`} className="mb-1.5 block">Bericht (optioneel)</Label>
                <Textarea
                  id={`ec-cta-message-${ctaType}`}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={cfg.placeholderMessage}
                  rows={3}
                  data-testid={`ec-cta-message-${ctaType}`}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={close}
                  className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm"
                  data-testid={`ec-cta-cancel-${ctaType}`}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary inline-flex items-center gap-2 px-5 py-2 rounded-md disabled:opacity-50 text-sm"
                  data-testid={`ec-cta-submit-${ctaType}`}
                >
                  {loading ? 'Verzenden…' : cfg.submitLabel}
                </button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
